// Ubicación en el repo: src/lib/SessionContext.jsx
// Fuente única de verdad sobre "quién está logueado y con qué nivel de
// autenticación (aal)". Se usa tanto para las guardas de ruta (App.jsx)
// como para que Login.jsx sepa si debe mostrar el paso de contraseña, el
// de verificación 2FA, o el de enrolamiento forzado — así no hay estado
// duplicado entre ambos.
import { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from './supabaseClient';

const SessionContext = createContext(undefined);

// status: 'loading' | 'ready'
// session: la sesión de Supabase, o null si no hay ninguna
// mfaSatisfied: true si no hace falta subir el aal AHORA (sin 2FA
//   configurado, o ya se completó el challenge de un factor existente).
//   Sigue el patrón recomendado por Supabase: comparar currentLevel vs
//   nextLevel de getAuthenticatorAssuranceLevel().
// needsMfaEnrollment: true si el rol del profesional exige 2FA
//   (professionals.mfa_required) y todavía no tiene ningún factor TOTP
//   verificado — ver DECISIONS.md #27. Es una condición aparte de
//   mfaSatisfied: sin ningún factor, currentLevel === nextLevel (los dos
//   aal1), así que mfaSatisfied da true por más que el rol exija 2FA — por
//   eso hace falta este chequeo adicional para forzar el enrolamiento en
//   vez de dejar pasar directo.
const initialState = { status: 'loading', session: null, mfaSatisfied: true, needsMfaEnrollment: false };

export function SessionProvider({ children }) {
  const [state, setState] = useState(initialState);

  useEffect(() => {
    let cancelled = false;

    async function computeState(session) {
      if (!session) {
        return { status: 'ready', session: null, mfaSatisfied: true, needsMfaEnrollment: false };
      }
      const { data } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      const mfaSatisfied = !data || data.currentLevel === data.nextLevel;

      let needsMfaEnrollment = false;
      if (mfaSatisfied) {
        // Solo tiene sentido preguntar esto cuando no hay un challenge
        // pendiente de un factor ya existente (eso ya lo cubre mfaSatisfied
        // = false más arriba).
        const { data: prof } = await supabase
          .from('professionals')
          .select('mfa_required')
          .eq('auth_user_id', session.user.id)
          .is('deleted_at', null)
          .maybeSingle();
        if (prof?.mfa_required) {
          const { data: factors } = await supabase.auth.mfa.listFactors();
          const hasVerified = factors?.totp?.some((f) => f.status === 'verified');
          needsMfaEnrollment = !hasVerified;
        }
      }

      return { status: 'ready', session, mfaSatisfied, needsMfaEnrollment };
    }

    supabase.auth.getSession().then(async ({ data }) => {
      const next = await computeState(data.session);
      if (!cancelled) setState(next);
    });

    const { data: subscription } = supabase.auth.onAuthStateChange(async (_event, session) => {
      const next = await computeState(session);
      if (!cancelled) setState(next);
    });

    return () => {
      cancelled = true;
      subscription.subscription.unsubscribe();
    };
  }, []);

  return <SessionContext.Provider value={state}>{children}</SessionContext.Provider>;
}

export function useSession() {
  const ctx = useContext(SessionContext);
  if (ctx === undefined) throw new Error('useSession debe usarse dentro de <SessionProvider>');
  return ctx;
}
